// src/Cart/cartSlice.js
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  tabCarted: "cartTab1",
  items: [],
  totalAmount: 0,
};

export const MAX_QUANTITY = 20;

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setTabCarted: (state, action) => {
      state.tabCarted = action.payload;
    },

    addToCart(state, action) {
      const {
        productID,
        variantID,
        productName,
        productPrice,
        price,
        frontImg,
        productReviews,
        quantity = 1,
        discount = 0,
        discount_amount = 0,
        variantImage,
        allImages,
      } = action.payload;

      // Validate inputs
      if (!productID || !variantID || !productPrice || productPrice <= 0) {
        console.error("Invalid product data", action.payload);
        return;
      }

      const validQuantity = Math.min(Math.max(1, quantity), MAX_QUANTITY);
      
      const existingItem = state.items.find(
        (item) => item.variantID === variantID,
      );

      if (existingItem) {
        const newQuantity = existingItem.quantity + validQuantity;

        if (newQuantity <= MAX_QUANTITY) {
          existingItem.quantity = newQuantity;
        } else {
          // If exceeds max, set to max
          existingItem.quantity = MAX_QUANTITY;
        }
        
        // Recalculate total amount after update
        state.totalAmount = state.items.reduce(
          (total, item) => total + (item.productPrice * item.quantity),
          0
        );
      } else {
        const finalQty = Math.min(validQuantity, MAX_QUANTITY);

        state.items.push({
          productID,
          variantID,
          productName,
          productPrice,
          price,
          discount,
          discount_amount,
          frontImg: variantImage || frontImg,
          allImages: allImages || [frontImg],
          productReviews: productReviews || [],
          quantity: finalQty,
          addedAt: Date.now(),
        });

        state.totalAmount += productPrice * finalQty;
      }
    },

    updateQuantity(state, action) {
      const { productID, variantID, quantity } = action.payload;
      
      // Validate quantity
      const newQuantity = Math.min(Math.max(1, quantity), MAX_QUANTITY);

      const item = state.items.find(
        (item) => item.productID === productID && item.variantID === variantID,
      );

      if (!item) {
        console.warn(`Item not found: ${productID} - ${variantID}`);
        return;
      }

      const oldQty = item.quantity;
      
      if (newQuantity === oldQty) return;
      
      item.quantity = newQuantity;
      
      // Recalculate total amount
      state.totalAmount = state.items.reduce(
        (total, item) => total + (item.productPrice * item.quantity),
        0
      );
    },

    removeFromCart(state, action) {
      const { productID, variantID } = action.payload;

      const itemIndex = state.items.findIndex(
        (item) => item.productID === productID && item.variantID === variantID,
      );

      if (itemIndex !== -1) {
        // Remove item
        state.items.splice(itemIndex, 1);
        
        // Recalculate total amount
        state.totalAmount = state.items.reduce(
          (total, item) => total + (item.productPrice * item.quantity),
          0
        );
      }
    },

    clearCart(state) {
      state.items = [];
      state.totalAmount = 0;
      state.tabCarted = "cartTab1"; // Reset tab on clear
    },

    recalculateTotal(state) {
      state.totalAmount = state.items.reduce(
        (total, item) => total + (item.productPrice * item.quantity),
        0,
      );
    },
    
    // NEW: Merge carts (useful for guest to logged-in user)
    mergeCarts(state, action) {
      const incomingItems = action.payload;
      
      incomingItems.forEach(incomingItem => {
        const existingItem = state.items.find(
          item => item.variantID === incomingItem.variantID
        );
        
        if (existingItem) {
          const newQuantity = Math.min(
            existingItem.quantity + incomingItem.quantity,
            MAX_QUANTITY
          );
          existingItem.quantity = newQuantity;
        } else {
          state.items.push({
            ...incomingItem,
            quantity: Math.min(incomingItem.quantity, MAX_QUANTITY)
          });
        }
      });
      
      // Recalculate total
      state.totalAmount = state.items.reduce(
        (total, item) => total + (item.productPrice * item.quantity),
        0
      );
    },
  },
});

// Actions export
export const {
  addToCart,
  removeFromCart,
  updateQuantity,
  setTabCarted,
  clearCart,
  recalculateTotal,
  mergeCarts, // New action
} = cartSlice.actions;

// ============= SELECTORS =============

// Basic Selectors
export const selectCartItems = (state) => state.cart.items;
export const selectCartTotalAmount = (state) => state.cart.totalAmount;
export const selectCartTab = (state) => state.cart.tabCarted;
export const selectCartItemCount = (state) => 
  state.cart.items.reduce((total, item) => total + item.quantity, 0);
export const selectUniqueItemsCount = (state) => state.cart.items.length;

// Item Selectors
export const selectCartItemByVariantId = (state, variantId) => 
  state.cart.items.find(item => item.variantID === variantId);

export const selectCartItemQuantity = (state, variantId) => {
  const item = state.cart.items.find(item => item.variantID === variantId);
  return item ? item.quantity : 0;
};

// Discount Selectors
export const selectCartItemsWithDiscount = (state) => {
  return state.cart.items.map((item) => ({
    ...item,
    originalPrice: item.price,
    discountAmount: item.discount_amount,
    discountPercent: item.discount,
    finalPrice: item.productPrice,
    subtotal: item.productPrice * item.quantity,
    originalSubtotal: item.price * item.quantity,
    savings: (item.price - item.productPrice) * item.quantity,
  }));
};

export const selectTotalDiscount = (state) => {
  return state.cart.items.reduce(
    (total, item) => total + (item.discount_amount * item.quantity),
    0,
  );
};

export const selectOriginalTotal = (state) => {
  return state.cart.items.reduce(
    (total, item) => total + (item.price * item.quantity),
    0,
  );
};

// Remove duplicate - use selectTotalDiscount instead
// export const selectDiscount = (state) => { ... } // REMOVED - duplicate

export const selectAverageDiscountPercent = (state) => {
  const items = state.cart.items;
  if (items.length === 0) return 0;
  
  const totalDiscountPercent = items.reduce(
    (total, item) => total + (item.discount * item.quantity),
    0,
  );
  
  const totalQuantity = items.reduce(
    (total, item) => total + item.quantity,
    0,
  );
  
  return totalQuantity > 0 ? totalDiscountPercent / totalQuantity : 0;
};

// Comprehensive Summary Selector
export const selectCartSummary = (state) => {
  const items = state.cart.items;
  
  const originalTotal = items.reduce(
    (total, item) => total + (item.price * item.quantity),
    0,
  );
  
  const finalTotal = items.reduce(
    (total, item) => total + (item.productPrice * item.quantity),
    0,
  );
  
  const totalDiscount = originalTotal - finalTotal;
  const discountPercentage = originalTotal > 0 
    ? (totalDiscount / originalTotal) * 100 
    : 0;
  
  const totalItems = items.reduce((count, item) => count + item.quantity, 0);
  
  return {
    originalTotal,
    finalTotal,
    totalDiscount,
    discountPercentage: Math.round(discountPercentage * 100) / 100, // Round to 2 decimals
    totalItems,
    uniqueItems: items.length,
    averageItemPrice: totalItems > 0 ? finalTotal / totalItems : 0,
  };
};

// Check if cart is empty
export const selectIsCartEmpty = (state) => state.cart.items.length === 0;

// Get cart item details for checkout
export const selectCheckoutDetails = (state) => ({
  items: state.cart.items.map(item => ({
    productID: item.productID,
    variantID: item.variantID,
    productName: item.productName,
    quantity: item.quantity,
    price: item.productPrice,
    originalPrice: item.price,
    discount: item.discount,
    discountAmount: item.discount_amount * item.quantity,
    subtotal: item.productPrice * item.quantity,
  })),
  totalAmount: state.cart.totalAmount,
  itemCount: state.cart.items.reduce((sum, item) => sum + item.quantity, 0),
});

export default cartSlice.reducer;